import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import LaunchButtonRow from '../common/LaunchButtonRow';
import { QUICK_ADD_SCOPE } from '../../hooks/useQuickAddTemplates';
import { canWriteCard } from '../../utils/permissions';
import { normalizeId } from '../../utils/ids';
import { PIN_KIND } from '../../services/favorites';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import { isDuplicateProfile } from '../../utils/duplicateProfiles';
import { accessContextFor, launchHomeFor } from '../../utils/collectionFamily';

/**
 * The path a launched template lands on: the new-item form for its card,
 * with the template id in the query so the form resolves it itself
 * (reload-safe — the prefill is recomputed on the API, never carried in the
 * URL).
 */
export const quickAddPath = (collectionId, template, home = null) => {
	// A parent's template for a sub-collection's card makes its record in
	// that sub-collection: the form opens THERE, and resolves the template
	// where it is defined (`templateIn`) — the sub-collection itself lists
	// none of its parent's templates (KPR-1084).
	const homeId = normalizeId(home?._id);
	const base = `/collections/${homeId || normalizeId(collectionId)}/items/new/${normalizeId(template?.card_id)}?template=${normalizeId(template?._id)}`;
	return homeId && homeId !== normalizeId(collectionId) ? `${base}&templateIn=${normalizeId(collectionId)}` : base;
};

/**
 * Navigates to a template's new-item form. The template's name rides along in
 * navigation state so the form can show "Quick add · <name>" at once; the
 * form still works from the URL alone (a pasted link, a reload).
 */
export function launchQuickAdd(navigate, collectionId, template, home = null) {
	navigate(quickAddPath(collectionId, template, home), {
		state: { quickAdd: { _id: normalizeId(template?._id), name: template?.name || '' } },
	});
}

const tierRank = scope => (scope === QUICK_ADD_SCOPE.COLLECTION ? 0 : 1);

/**
 * The templates a caller may launch, ordered for a launch row: collection
 * tier first, then the caller's own, name order within each.
 *
 * A template whose card the caller cannot WRITE is excluded — its resolve
 * would answer 403 and the form after it a 403 on save — and so is one whose
 * card is not in the definitions cache (nothing to render the tile from, and
 * a card the cache lacks is one this caller cannot read). A DUPLICATE PROFILE
 * is excluded too: it copies from an existing item, so it launches from that
 * item's Duplicate ▾ (utils/duplicateProfiles.js) — a launcher here would
 * open a form with nothing to copy.
 *
 * SUB-COLLECTIONS: at a parent, a template may name a sub-collection's card —
 * `home` is that sub-collection, where it launches and where the caller must
 * be able to write. A sub-collection launches only its own templates: it
 * inherits none from the collections above it (owner ruling 2026-10-21,
 * KPR-1084).
 *
 * Returns [{ template, card, scope, home }].
 */
export function selectQuickAddTemplates({ collection, templates, cardsById }) {
	return (templates || [])
		.filter(template => !isDuplicateProfile(template))
		.map(template => {
			const cardId = normalizeId(template?.card_id);
			const card = cardId ? cardsById?.[cardId] : null;
			if (!card) return null;
			if (collection && !canWriteCard(accessContextFor(collection, cardId), cardId)) return null;
			return {
				template,
				card,
				scope: template?.scope === QUICK_ADD_SCOPE.COLLECTION ? QUICK_ADD_SCOPE.COLLECTION : QUICK_ADD_SCOPE.USER,
				home: collection ? launchHomeFor(collection, cardId) : null,
			};
		})
		.filter(Boolean)
		.sort((a, b) => (
			tierRank(a.scope) - tierRank(b.scope)
			|| (a.template?.name || '').localeCompare(b.template?.name || '')
		));
}

/**
 * Quick add templates as one-click launchers (see LaunchButtonRow for the
 * row rule). The button's tile is the template's CARD — a template has no
 * identity of its own beyond its name — and the tooltip names the card.
 *
 * `onLaunch(template)` overrides the default navigation so a host can do its
 * own housekeeping first (the Add item modal closes itself before it
 * navigates). Renders nothing when the caller has no launchable template.
 */
export default function QuickAddButtons({
	collectionId,
	collection,
	templates,
	cardsById,
	onLaunch,
	max,
	getPopupContainer,
	// Offer a Home pin toggle on each button (the collection home does; the
	// Add item modal, a place to launch from, does not).
	pinToHome = false,
}) {
	const navigate = useNavigate();
	const favoritesCtx = useOptionalFavorites();
	const canPin = pinToHome && typeof favoritesCtx?.togglePin === 'function';

	const launchable = useMemo(
		() => selectQuickAddTemplates({ collection, templates, cardsById }),
		[collection, templates, cardsById]
	);

	const entries = useMemo(() => launchable.map(({ template, card, scope, home }) => ({
		key: normalizeId(template._id),
		// A Home pin launches the template where it is defined; one that
		// launches elsewhere (a sub-collection's card) offers none.
		pin: canPin && !home ? {
			pinned: favoritesCtx.isPinned(PIN_KIND.QUICK_ADD, normalizeId(template._id), collectionId),
			onToggle: () => favoritesCtx.togglePin(PIN_KIND.QUICK_ADD, { targetId: normalizeId(template._id), collectionId: normalizeId(collectionId) }),
		} : undefined,
		record: card,
		kind: 'card',
		label: template.name || '(untitled quick add)',
		// Where the item is MADE (a sub-collection's card): the button reads
		// qualified.
		collection: home || undefined,
		collectionNote: home ? (identity => `Adds the item in ${identity.name}`) : undefined,
		scope,
		// The full label leads: on a phone the button ellipsizes a long one,
		// and the tooltip (hover and keyboard focus) is where it reads whole.
		title: `${template.name || '(untitled quick add)'} · Quick add · ${card?.name || 'card'}`,
		tip: true,
		onLaunch: () => {
			if (typeof onLaunch === 'function') {
				onLaunch(template, home);
				return;
			}
			launchQuickAdd(navigate, collectionId, template, home);
		},
	})), [launchable, onLaunch, navigate, collectionId, canPin, favoritesCtx]);

	if (!entries.length) return null;

	return (
		<LaunchButtonRow
			entries={entries}
			max={max}
			ariaLabel="Quick add"
			getPopupContainer={getPopupContainer}
		/>
	);
}
